import React from 'react';
import PropTypes from 'prop-types';
import { compact } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { connect } from 'react-redux';
import { CardImage } from 'site-modules/shared/components/card-image/card-image';
import { VehicleImage } from 'site-modules/shared/components/upper-funnel/vehicle-image/vehicle-image';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';

import './compare-vehicle-tray.scss';

export const COMPARE_VEHICLE_TRAY_ID = 'compare-vehicle-tray';

export function handleChangeGenerator({ onSelectCar, id }) {
  return event => onSelectCar(event, { id });
}

export function onBackFocusLink(selector) {
  return () => document.querySelector(selector).focus();
}

function CompareVehicleTrayUI({
  clearSelectedCars,
  handleCompareClick,
  onSelectCar,
  selectedCars,
  isMobile = false,
  trackingParent = null,
  backFocusLink = null,
}) {
  if (selectedCars.length === 0) {
    return null;
  }

  return (
    <Row
      className="container compare-vehicle-tray-container mx-auto justify-content-center pb-1_5 pb-md-2"
      data-hidedelighted
      data-tracking-parent={trackingParent}
    >
      <Col xs={12} lg={10} xl={9}>
        <Row
          className="compare-vehicle-tray bg-white rounded align-items-center py-0_5"
          role="dialog"
          aria-labelledby="compare-vehicle-tray-title"
          aria-describedby="compare-vehicle-tray-desc"
        >
          <ScrollElement id={COMPARE_VEHICLE_TRAY_ID} className="visually-hidden">
            <div aria-live="assertive" id="compare-vehicle-live-region">
              Compare tray opened.
            </div>
            <h2 id="compare-vehicle-tray-title">Compare selected vehicles</h2>
            <div id="compare-vehicle-tray-desc">
              You can compare selected vehicles here. Use the buttons to remove vehicles or clear all.
            </div>
          </ScrollElement>
          {backFocusLink && (
            <button
              onClick={onBackFocusLink(backFocusLink.selector)}
              className="skip-link p-0 ms-0_5 btn btn-link text-transform-none visually-hidden-focusable"
            >
              {backFocusLink.label}
            </button>
          )}
          {!isMobile && (
            <Col md={7} className="px-2 pe-md-0_25">
              <Row>
                {selectedCars.map(selectedCar => {
                  const { baseStyleId, image, modelYear, submodelSlug, price, bodyType } = selectedCar;
                  const id = selectedCar.id || baseStyleId;

                  const { year, makeName, modelName } = modelYear || {};
                  const vehicleName = image ? image.alt : compact([year, makeName, modelName]).join(' ');
                  const removeTitle = compact(['Deselect', vehicleName, price ? `priced at ${price}` : '']).join(' ');

                  return (
                    <Col xs={3} key={id} className="px-0_25 position-relative">
                      {/* for non type rankings images */}
                      {image ? (
                        <div className="card-photo text-center rounded ed-zdepth-1 h-100 d-flex align-items-center">
                          <CardImage className="w-100" photoUrl={image.url} bodyType={bodyType} altText={image.alt} />
                        </div>
                      ) : (
                        <VehicleImage
                          imageClassName="rounded ed-zdepth-1"
                          modelYear={modelYear}
                          submodelSlug={submodelSlug}
                          widthToFit={100}
                          heightToFit={70}
                        />
                      )}
                      <Button
                        color="link"
                        onClick={handleChangeGenerator({ onSelectCar, id })}
                        className="remove-thumbnail-btn rounded text-white p-0 icon-cross2 size-10"
                        title={removeTitle}
                        aria-label={removeTitle}
                      />
                    </Col>
                  );
                })}
              </Row>
            </Col>
          )}
          <Col xs={12} md={5} className="px-1_5 px-lg-1_25 px-xl-1_5 py-0_25 d-flex justify-content-between">
            <Button color="link" className="clear-btn size-16 p-0" onClick={clearSelectedCars}>
              <i className="small icon-cross2" aria-hidden /> Clear all
              <span className="visually-hidden"> selected cars</span>
            </Button>
            <Button
              color="blue-50"
              className="compare-btn size-16 py-0_5 px-1"
              onClick={handleCompareClick}
              data-tracking-id="compare_button_click"
            >
              Compare selected {isMobile && `(${selectedCars.length})`}
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

CompareVehicleTrayUI.propTypes = {
  clearSelectedCars: PropTypes.func.isRequired,
  handleCompareClick: PropTypes.func.isRequired,
  selectedCars: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectCar: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  trackingParent: PropTypes.string,
  backFocusLink: PropTypes.shape({
    label: PropTypes.string,
    selector: PropTypes.string,
  }),
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const CompareVehicleTray = connect(mapStateToProps)(CompareVehicleTrayUI);
